import PocketBase from 'pocketbase';
import { isTauriApp } from '../utils/tauriHelper';
import { recordPocketBaseResponseReachability } from './pocketbaseReachability';

const DEFAULT_BASE_URL = 'https://thryveloop.com';
const PB_DEFAULT_REQUEST_TIMEOUT_MS = 20000;
const PB_LLM_PROXY_TIMEOUT_MS = 130000;
const ALLOWED_HOSTS = new Set([
  'thryveloop.com',
  'www.thryveloop.com',
  'staging.thryveloop.com',
  'staging.thryveloop.app',
  'localhost:3000',
  'localhost:8090',
  '127.0.0.1:8090'
]);

const sanitizeBaseUrl = (url) => {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed.replace(/\/+$/, '');
  }
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`.replace(/\/+$/, '');
  }
  if (trimmed.startsWith('/')) {
    // Relative URLs won't work with the PocketBase SDK - fall back to default
    return '';
  }
  return `https://${trimmed.replace(/\/+$/, '')}`;
};

const isAllowedHost = (host) => {
  if (!host) return false;
  return ALLOWED_HOSTS.has(host.toLowerCase());
};

const resolvePocketBaseUrl = () => {
  const candidates = [];

  // Desktop build can pin its own endpoint via dedicated env var
  if (isTauriApp()) {
    candidates.push(process.env.REACT_APP_TAURI_PB_URL);
  }

  candidates.push(process.env.REACT_APP_POCKETBASE_URL);

  for (const candidate of candidates) {
    const envUrl = sanitizeBaseUrl(candidate);
    if (!envUrl) {
      continue;
    }
    try {
      const { host } = new URL(envUrl);
      if (isAllowedHost(host)) {
        return envUrl;
      }
      console.warn(`[PocketBase] Env URL host not allowlisted (${host}), ignoring value.`);
    } catch (e) {
      console.warn('[PocketBase] Invalid PocketBase URL provided via env, ignoring.', e);
    }
  }

  if (!process.env.REACT_APP_POCKETBASE_URL) {
    console.warn('[PocketBase] REACT_APP_POCKETBASE_URL missing – defaulting to production backend');
  }

  if (typeof window !== 'undefined') {
    const { protocol, host } = window.location;
    if ((protocol === 'http:' || protocol === 'https:') && isAllowedHost(host)) {
      return `${protocol}//${host}`;
    }
    if (host && !isAllowedHost(host)) {
      console.warn(`[PocketBase] Host ${host} not allowlisted, using default backend URL.`);
    }
  }

  return DEFAULT_BASE_URL;
};

const baseUrl = resolvePocketBaseUrl();
console.log(`[PocketBase] Using base URL: ${baseUrl}`);

const resolveRequestTimeoutMs = (url = '') => {
  const normalized = String(url || '').toLowerCase();
  if (normalized.includes('/api/self/llm-proxy')) {
    return PB_LLM_PROXY_TIMEOUT_MS;
  }
  return PB_DEFAULT_REQUEST_TIMEOUT_MS;
};

const createTimedSignal = (timeoutMs, externalSignal = null) => {
  const timeout = Math.max(500, Number(timeoutMs) || PB_DEFAULT_REQUEST_TIMEOUT_MS);
  const hasAbortSignalTimeout = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function';
  const hasAbortSignalAny = typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function';

  if (hasAbortSignalTimeout) {
    const timeoutSignal = AbortSignal.timeout(timeout);
    if (!externalSignal) {
      return timeoutSignal;
    }
    if (hasAbortSignalAny) {
      return AbortSignal.any([externalSignal, timeoutSignal]);
    }
    const controller = new AbortController();
    const forwardAbort = () => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    };
    if (externalSignal.aborted || timeoutSignal.aborted) {
      forwardAbort();
      return controller.signal;
    }
    externalSignal.addEventListener('abort', forwardAbort, { once: true });
    timeoutSignal.addEventListener('abort', forwardAbort, { once: true });
    return controller.signal;
  }

  if (typeof AbortController === 'undefined') {
    return externalSignal || undefined;
  }

  const controller = new AbortController();
  const timer = setTimeout(() => {
    if (!controller.signal.aborted) {
      controller.abort();
    }
  }, timeout);

  const cleanup = () => clearTimeout(timer);
  controller.signal.addEventListener('abort', cleanup, { once: true });

  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
      return controller.signal;
    }
    externalSignal.addEventListener('abort', () => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    }, { once: true });
  }

  return controller.signal;
};

// Initialize PocketBase client
export const pb = new PocketBase(baseUrl);
export const pocketbaseBaseUrl = baseUrl;

// Enable auto cancellation for duplicate requests
pb.autoCancellation(false);

pb.beforeSend = (url, options = {}) => {
  const timeoutMs = resolveRequestTimeoutMs(url);
  return {
    url,
    options: {
      ...options,
      signal: createTimedSignal(timeoutMs, options?.signal || null)
    }
  };
};

pb.afterSend = (response, data) => {
  recordPocketBaseResponseReachability(response, { source: 'sdk' });
  return data;
};

// Optional: Subscribe to auth changes
pb.authStore.onChange((auth) => {
  console.log('Auth state changed:', auth);
});

export default pb;
